// Revolt Textarea Component
// Advanced textarea with auto-resizing and character counting
// Inspired by AGC textarea with Revolt design system integration

import React, { useRef, useEffect, useState, useCallback } from 'react';
import './RevoltTextarea.css';

export function RevoltTextarea({
  variant = 'primary', // 'primary' or 'outline'
  size = 'default', // 'default' or 'sm'
  value = '',
  onChange,
  onSubmit,
  maxLength,
  minLength,
  placeholder = '',
  disabled = false,
  required = false,
  className = '',
  rows = 3,
  minRows = 2,
  maxRows = 10,
  autoResize = true,
  ...props
}) {
  const textareaRef = useRef(null);
  const [currentLength, setCurrentLength] = useState(0);

  // Auto-resize functionality
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !autoResize) return;

    // Reset height to measure content
    textarea.style.height = 'auto';

    const computed = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.2;
    const paddingY = parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom);
    const borderY = parseFloat(computed.borderTopWidth) + parseFloat(computed.borderBottomWidth);

    const minHeight = minRows * lineHeight + paddingY + borderY;
    const maxHeight = maxRows * lineHeight + paddingY + borderY;

    // Calculate content height
    const contentHeight = textarea.scrollHeight + borderY;

    // Clamp height between min and max
    const clampedHeight = Math.max(minHeight, Math.min(contentHeight, maxHeight));
    textarea.style.height = `${clampedHeight}px`;

    // Show scrollbar if content exceeds max height
    textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  }, [autoResize, minRows, maxRows]);

  // Handle input changes
  const handleInputChange = (e) => {
    const newValue = e.target.value;

    if (maxLength && newValue.length > maxLength) {
      return; // Prevent input beyond maxLength
    }

    setCurrentLength(newValue.length);

    if (onChange) {
      onChange(e);
    }

    // Auto-resize after content change
    setTimeout(adjustHeight, 0);
  };

  // Handle key down for submit functionality
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
      e.preventDefault();
      onSubmit(e, e.target.value);
    }
    // Shift+Enter creates new line (default behavior)
  };

  // Update character count and adjust height on mount and value changes
  useEffect(() => {
    setCurrentLength(value.length);
    adjustHeight();
  }, [value, adjustHeight]);

  // Adjust height on window resize
  useEffect(() => {
    const handleResize = () => adjustHeight();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [adjustHeight]);

  // Build CSS classes
  const textareaClasses = [
    'revolt-textarea',
    `revolt-textarea-${variant}`,
    `revolt-textarea-${size}`,
    className
  ].filter(Boolean).join(' ');

  const containerClasses = [
    'revolt-textarea-container',
    disabled && 'disabled'
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <textarea
        ref={textareaRef}
        className={textareaClasses}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        minLength={minLength}
        rows={rows}
        {...props}
      />

      {maxLength && (
        <div className="revolt-textarea-counter">
          <span className="current-count">{currentLength}</span>
          <span className="max-count">/{maxLength}</span>
        </div>
      )}
    </div>
  );
}
